import { Navigate } from "react-router-dom";
import { resolveClipSourceStatus } from "@/lib/clips/sourceReadiness";
import Layout from "@/components/Layout";
import { LoadingCard, LoadingCardFrame } from "@/components/LoadingCard";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import ProjectEditor from "@/components/ProjectEditor";
import { useQuery } from "@apollo/client";
import { GET_MY_LIBRARY, GET_MY_DATA } from "@/queries/queries";
import { getProjectStatusLabel } from "@/constants/project-status";
import type { MyLibraryQuery, MyLibraryQueryVariables } from "@/generated/clip-source-pages";
import { useRenderLibraryClipMutation, useUpdateMyProjectMutation } from "@/generated/graphql";
import { useLibraryPolling } from "@/hooks/use-polling";
import { etaSecondsToMinutes } from "@/lib/format/etaMinutes";
import { useCallback } from "react";
import type { PersistedPostLayoutBlob } from "@/lib/layouts/persistPostLayout";
import { buildUpdateMyProjectOptimistic } from "@/lib/projects/buildUpdateMyProjectOptimistic";
import { FailedProjectNotice } from "@/components/projects/FailedProjectNotice";

const LibraryCopilotPage = ({ variant = 'editor' }: { variant?: 'editor' | 'studio' }) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialClipId = searchParams.get('clip') || undefined;

  const { data, loading, error, refetch } = useQuery<MyLibraryQuery, MyLibraryQueryVariables>(GET_MY_LIBRARY, {
    variables: { id: id as string },
    skip: !id,
  });

  const [renderLibraryClipMutation] = useRenderLibraryClipMutation({ refetchQueries: [{ query: GET_MY_DATA }] });

  const renderV2 = useCallback(async (variables: { id: string; input: any }) => {
    return renderLibraryClipMutation({ variables });
  }, [renderLibraryClipMutation]);

  const [updateMyProjectMutation] = useUpdateMyProjectMutation();

  const initialLibrary = data?.myLibrary;
  const status = initialLibrary?.curation_status;
  const { data: polledLibrary } = useLibraryPolling(id, {
    enabled: !!status && !['success', 'failure', 'expired'].includes(status),
  });

  const library = polledLibrary ?? initialLibrary;
  const effectiveStatus = polledLibrary?.curation_status || status;
  const statusLabel = getProjectStatusLabel(effectiveStatus);
  const etaSeconds = polledLibrary?.eta_seconds ?? library?.eta_seconds;
  const etaMinutes = etaSecondsToMinutes(etaSeconds);

  const handleSpeakerLayoutChange = useCallback(
    (layout: number) => {
      if (!id) return;
      updateMyProjectMutation({
        variables: { id, input: { speaker_layout: layout } },
        optimisticResponse: buildUpdateMyProjectOptimistic(
          { id, ...library },
          { speaker_layout: layout },
        ),
      }).catch((err) => {
        console.error("Failed to persist speaker_layout", err);
      });
    },
    [id, library, updateMyProjectMutation],
  );

  const handlePostLayoutChange = useCallback(
    (post_layout: PersistedPostLayoutBlob | null) => {
      if (!id) return;
      updateMyProjectMutation({
        variables: { id, input: { post_layout } },
        optimisticResponse: buildUpdateMyProjectOptimistic(
          { id, ...library },
          { post_layout },
        ),
      }).catch((err) => {
        console.error("Failed to persist post_layout", err);
      });
    },
    [id, library, updateMyProjectMutation],
  );

  if (loading) {
    return (
      <Layout>
        <LoadingCardFrame>
          <LoadingCard message={t('projectEditor.loadingProject')} />
        </LoadingCardFrame>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-2">
            <p className="text-sm text-destructive">{t('projectEditor.errorLoadingProject')}</p>
            <p className="text-xs text-muted-foreground">{error.message}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!library) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-sm text-muted-foreground">{t('projectEditor.projectNotFound')}</p>
        </div>
      </Layout>
    );
  }

  if (library && resolveClipSourceStatus(library.source_status) !== 'ready'
      && library.curation_status === 'success') {
    return <Navigate to={`/library/${id}/clips`} replace />;
  }

  if (effectiveStatus === 'failure') {
    return (
      <Layout>
        <FailedProjectNotice
          kind="library"
          id={id}
          failureCode={(polledLibrary ?? library)?.failure_code}
          videoUrl={library?.compressed_base_video_url}
          durationSeconds={library?.duration_seconds}
          rangeStart={library?.start_time}
          onConverted={() => refetch()}
        />
      </Layout>
    );
  }

  if (effectiveStatus === 'expired') {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-sm text-muted-foreground">{t('projectEditor.projectExpired')}</p>
        </div>
      </Layout>
    );
  }

  if (effectiveStatus && effectiveStatus !== 'success') {
    return (
      <Layout>
        <LoadingCardFrame>
          <LoadingCard
            message={statusLabel}
            status={effectiveStatus}
            etaLabel={etaMinutes != null
              ? t('projectEditor.estimatedTime', { minutes: etaMinutes })
              : undefined}
          />
        </LoadingCardFrame>
      </Layout>
    );
  }

  // Adapt library data to the shape ProjectEditor expects
  const projectData = {
    ...library,
    // ProjectEditor accesses clips via (project as any).clips
    clips: (library as any).clips || [],
    curated_clips: (library as any).curated_clips || [],
    transcription: (library as any).transcription || null,
    transcription_url: (library as any).transcription_url || null,
    brand_kit: (library as any).brand_kit || null,
  };

  return (
    <Layout>
      <ProjectEditor
        variant={variant}
        project={projectData as any}
        initialClipId={initialClipId}
        clipsPagePath="/clips"
        renderV2={renderV2}
        isLibrary
        onSpeakerLayoutChange={handleSpeakerLayoutChange}
        onPostLayoutChange={handlePostLayoutChange}
      />
    </Layout>
  );
};

export default LibraryCopilotPage;
